import { Text } from '@chakra-ui/react';
import type { SelectOption } from '@diamond/shared/types';
import {
  ChakraStylesConfig,
  Select as ReactSelect,
  SingleValue,
} from 'chakra-react-select';

const getOptionBackgroundColor = (
  isSelected: boolean,
  isFocused: boolean
): string => {
  if (isSelected) return 'var(--chakra-colors-blue)';
  if (isFocused) return 'var(--chakra-colors-gray-100)';
  return 'white';
};

const getOptionActiveBackgroundColor = (isSelected: boolean): string => {
  if (isSelected) return 'var(--chakra-colors-blue)';
  return 'var(--chakra-colors-gray-200)';
};

const selectStyles: ChakraStylesConfig<SelectOption, false> = {
  container: (base) => ({ ...base, width: '100%' }),
  menu: (base) => ({ ...base, zIndex: 10 }),
  menuList: (base) => ({ ...base, padding: 0 }),
  indicatorSeparator: () => ({ display: 'none' }),
  control: (base, state) => ({
    ...base,
    minHeight: '40px',
    borderRadius: 'var(--chakra-radii-lg)',
    borderColor: 'var(--chakra-colors-gray-200)',
    fontSize: 'var(--chakra-fontSizes-sm)',
    ...(state.isFocused && {
      borderColor: 'var(--chakra-colors-blue-500)',
      boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)',
    }),
    ...(state.isDisabled && {
      backgroundColor: 'var(--chakra-colors-gray-100)',
      borderColor: 'var(--chakra-colors-gray-200)',
      opacity: 1,
      cursor: 'not-allowed',
    }),
  }),
  singleValue: (base, state) => ({
    ...base,
    fontSize: 'var(--chakra-fontSizes-sm)',
    ...(state.isDisabled && {
      color: 'var(--chakra-colors-gray-600)',
    }),
  }),
  placeholder: (base) => ({
    ...base,
    fontSize: 'var(--chakra-fontSizes-sm)',
    color: 'var(--chakra-colors-gray-400)',
  }),
  option: (base, state) => ({
    ...base,
    fontSize: 'var(--chakra-fontSizes-sm)',
    padding: '8px 12px',
    backgroundColor: getOptionBackgroundColor(
      state.isSelected,
      state.isFocused
    ),
    color: state.isSelected ? 'white' : 'inherit',
    '&:active': {
      backgroundColor: getOptionActiveBackgroundColor(state.isSelected),
    },
  }),
};

export interface PurchaseProductSelectProps {
  value: SelectOption | null;
  onChange: (option: SelectOption | null) => void;
  options: SelectOption[];
  placeholder?: string;
  isDisabled?: boolean;
  isClearable?: boolean;
}

export function PurchaseProductSelect({
  value,
  onChange,
  options,
  placeholder = 'Pilih produk',
  isDisabled = false,
  isClearable = true,
}: PurchaseProductSelectProps) {
  const handleChange = (newValue: SingleValue<SelectOption>) => {
    onChange(newValue);
  };

  const hasNoOptions = options.length === 0;

  return (
    <ReactSelect<SelectOption, false>
      value={value}
      onChange={handleChange}
      options={options}
      placeholder={placeholder}
      isDisabled={isDisabled || hasNoOptions}
      isClearable={isClearable}
      hideSelectedOptions={false}
      chakraStyles={selectStyles}
      noOptionsMessage={() => (
        <Text fontSize="sm" color="gray.500">
          Tidak ada produk tersedia
        </Text>
      )}
    />
  );
}
